import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import EmployeeCard from "../../components/admin/employee-card";
import OfficialCard from "../../components/admin/official-card";
import ReportCard from "../../components/admin/report-card";
import { ArrowLeftFromLine, RotateCcw } from "lucide-react";
import SalaryCard from "../../components/admin/salary-card";
import PayCard from "../../components/admin/pay-card";
import GeneralInformation from "../../modal/employee/GeneralInformationEdit"
import Identities from "../../modal/employee/IdentitiesEdit"
import Address from "../../modal/employee/AddressEdit"
import Contract from "../../modal/employee/ContractEdit"
import Eligibility from "../../modal/employee/EligibilityEdit"
import AttendanceRule from "../../modal/employee/AttendanceRuleEdit"
import Dependents from "../../modal/employee/DependentsEdit"
import Leaves from "../../modal/employee/LeavesEdit"
import Payment from "../../modal/employee/PaymentEdit"
import SalaryDetails from "../../modal/employee/SalaryDetailsEdit"
import { getRequest } from "../../service";
import { message, Spin, Dropdown } from "antd";
import { getEmployeeData } from "../../utils/localStorage";

const ViewEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const employeeData = getEmployeeData();
  const role = employeeData.role;
  const employeePermissions =
  employeeData?.designation?.permissions?.Employee?.['All Employee Access'] || [];
  const canEdit = role === "companyadmin" || employeePermissions.includes("edit");
  const [employee, setEmployee] = useState(null);
  const [employeeLoading, setEmployeeLoading] = useState(true);
  const [generalModalOpen, setGeneralModalOpen] = useState(false);
  const [identitiesModalOpen, setIdentitiesModalOpen] = useState(false);
  const [addressModalOpen, setaddressModalOpen] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [eligibilityModalOpen, setEligibilityModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [salaryModalOpen, setSalaryModalOpen] = useState(false);
  const [leavesModalOpen, setLeavesModalOpen] = useState(false);
  const [dependentsModalOpen, setDependentsModalOpen] = useState(false);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const fetchEmployees = async () => {
    try {
      const response = await getRequest(`/api/employees/${id}`);
      if (response && response.employee) {
        setEmployee(response.employee);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      message.error('Failed to fetch employees');
    } finally {
      setEmployeeLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleEditOption = (option) => {
    // Close dropdown first
    setDropdownOpen(false);
    
    // Use setTimeout to ensure dropdown is closed before opening modal
    setTimeout(() => {
      switch(option) {
        case 'general':
          setGeneralModalOpen(true);
          break;
        case 'identities':
          setIdentitiesModalOpen(true);
          break;
        case 'address':
          setaddressModalOpen(true);
          break;
        case 'contract':
          setContractModalOpen(true);
          break;
        case 'eligibility':
          setEligibilityModalOpen(true);
          break;
        case 'payment':
          setPaymentModalOpen(true);
          break;
        case 'salary':
          setSalaryModalOpen(true);
          break;
        case 'leaves':
          setLeavesModalOpen(true);
          break;
        case 'dependents':
          setDependentsModalOpen(true);
          break;
        case 'attendance':
          setAttendanceModalOpen(true);
          break;
        default:
          break;
      }
    }, 100);
  };

  const menuItems = [
    {
      key: 'general',
      label: 'General Information',
      onClick: () => handleEditOption('general')
    },
    {
      key: 'identities',
      label: 'Identities',
      onClick: () => handleEditOption('identities')
    },
    {
      key: 'address',
      label: 'Address',
      onClick: () => handleEditOption('address')
    },
    {
      key: 'contract',
      label: 'Contract Information',
      onClick: () => handleEditOption('contract')
    },
    {
      key: 'eligibility',
      label: 'Eligibility',
      onClick: () => handleEditOption('eligibility')
    },
    {
      key: 'payment',
      label: 'Payment Method',
      onClick: () => handleEditOption('payment')
    },
    {
      key: 'salary',
      label: 'Salary Details',
      onClick: () => handleEditOption('salary')
    },
    {
      key: 'leaves',
      label: 'Leaves',
      onClick: () => handleEditOption('leaves')
    },
    {
      key: 'dependents',
      label: 'Dependents',
      onClick: () => handleEditOption('dependents')
    },
    {
      key: 'attendance',
      label: 'Time & Attendance Rule',
      onClick: () => handleEditOption('attendance')
    }
  ];

  if (employeeLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen gap-2 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-lg">Employee Information</h4>
          {/* <RotateCcw 
            className="h-5 w-5 cursor-pointer " 
            onClick={fetchEmployees}
          /> */}
        </div>
        <div className="flex gap-3">
          <Dropdown
            menu={{ items: menuItems }}
            placement="bottomRight"
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            disabled={!canEdit}
          >
            <button 
              className={`px-4 py-1.5 rounded-md text-md transition-all ${
                canEdit 
                  ? 'cursor-pointer bg-active-color text-white hover:bg-opacity-90' 
                  : 'cursor-not-allowed bg-primary text-white opacity-50'
              }`}
              disabled={!canEdit}
            >
              Edit Profile
            </button>
          </Dropdown>
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 border border-active-color text-active-color px-4 py-1.5 text-md rounded-md  transition-all cursor-pointer"
          >
            <ArrowLeftFromLine size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-4">
        <div className="lg:col-span-3">
          <EmployeeCard employee={employee} />
        </div>
        <div className="lg:col-span-4 flex flex-col gap-4">
          <OfficialCard employee={employee} />
          <SalaryCard employee={employee} />
          <PayCard employee={employee} />
        </div>
        <div className="lg:col-span-5">
          <ReportCard employeeId={id} leaveId={employee.annual_leave_policy} />
        </div>
      </div>

      <GeneralInformation
        open={generalModalOpen}
        employee={employee}
        onRequestClose={() => {
          setGeneralModalOpen(false);
          fetchEmployees();
        }}
      />
      <Identities
        open={identitiesModalOpen}
        employee={employee}
        onRequestClose={() => {
          setIdentitiesModalOpen(false);
          fetchEmployees();
        }}
      />
      <Address
        open={addressModalOpen}
        employee={employee}
        onRequestClose={() => {
          setaddressModalOpen(false);
          fetchEmployees();
        }}
      />
      <Contract
        open={contractModalOpen}
        employee={employee}
        onRequestClose={() => {
          setContractModalOpen(false);
          fetchEmployees();
        }}
      />
      <Eligibility
        open={eligibilityModalOpen}
        employee={employee}
        onRequestClose={() => {
          setEligibilityModalOpen(false);
          fetchEmployees();
        }}
      />
      <Payment
        open={paymentModalOpen}
        employee={employee}
        onRequestClose={() => {
          setPaymentModalOpen(false);
          fetchEmployees();
        }}
      />
      <SalaryDetails
        open={salaryModalOpen}
        employee={employee}
        onRequestClose={() => {
          setSalaryModalOpen(false);
          fetchEmployees();
        }}
      />
      <Leaves
        open={leavesModalOpen}
        employee={employee}
        onRequestClose={() => {
          setLeavesModalOpen(false);
          fetchEmployees();
        }}
      />
      <Dependents
        open={dependentsModalOpen}
        employee={employee}
        onRequestClose={() => {
          setDependentsModalOpen(false);
          fetchEmployees();
        }}
      />
      <AttendanceRule
        open={attendanceModalOpen}
        employee={employee}
        onRequestClose={() => {
          setAttendanceModalOpen(false);
          fetchEmployees();
        }}
      />
    </div>
  );
};

export default ViewEmployee;
